import React from 'react';
import ReactMarkdown from 'react-markdown';

interface MessageRendererProps {
  content: string;
  role: 'user' | 'assistant';
  className?: string;
}

const MessageRenderer: React.FC<MessageRendererProps> = ({ content, role, className }) => {
  console.log('🎨 MessageRenderer renderizado:', { role, contentLength: content?.length, content: content });
  
  // Para mensagens do usuário, renderizar como texto simples
  if (role === 'user') {
    return (
      <p className={`text-sm whitespace-pre-wrap ${className || 'mb-2'}`}>
        {content}
      </p>
    );
  }

  // Pré-processar o conteúdo para melhorar a formatação
  const processedContent = content
    // Melhorar formatação de seções com emojis
    .replace(/^(✨|⚠️|🎯|💡|📅|👥|💬|🔄)\s*\*\*(.*?)\*\*/gm, '> **$1 $2**')
    // Destacar insights importantes
    .replace(/^(📋|📁|🧠|🎯)\s*(.*?):/gm, '### $1 $2:')
    // Melhorar formatação de listas de documentos
    .replace(/^(\d+)\.\s*(⭐|📄)\s*\*\*(.*?)\*\*/gm, '$1. **$2 $3**');

  // Para mensagens do assistente, renderizar Markdown
  return (
    <div className={`text-sm mb-2 prose prose-sm max-w-none ${className || ''}`}>
      <ReactMarkdown
        components={{
          // Customizar elementos HTML gerados
          h1: ({ children }) => (
            <h1 className="text-lg font-bold mb-3 text-gray-900 border-b border-gray-200 pb-1">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-base font-bold mb-2 text-gray-800 flex items-center">
              <span className="mr-2">📋</span>{children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-bold mb-1 text-gray-700 flex items-center">
              <span className="mr-1">📁</span>{children}
            </h3>
          ),
          
          // Parágrafos
          p: ({ children }) => <p className="mb-2 leading-relaxed text-gray-900">{children}</p>,
          
          // Texto em negrito
          strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
          
          // Texto em itálico
          em: ({ children }) => <em className="italic text-gray-800">{children}</em>,
          
          // Listas
          ul: ({ children }) => (
            <ul className="list-none mb-3 space-y-1.5 text-gray-900 pl-0">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-none mb-3 space-y-1.5 text-gray-900 pl-0 counter-reset-[item]">
              {children}
            </ol>
          ),
          li: ({ children }) => {
            // Detectar se é uma lista numerada ou com marcadores
            const content = children?.toString() || '';
            
            // Lista de documentos (detectar padrões comuns)
            if (content.includes('📄') || content.includes('⭐') || content.includes('📍')) {
              return (
                <li className="bg-gray-50 rounded-lg p-3 mb-2 border-l-4 border-blue-200 text-gray-900">
                  <div className="text-sm leading-relaxed">{children}</div>
                </li>
              );
            }
            
            // Lista simples
            return (
              <li className="flex items-start text-gray-900 mb-1">
                <span className="text-blue-500 mr-2 mt-0.5">•</span>
                <span className="flex-1">{children}</span>
              </li>
            );
          },
          
          // Código inline
          code: ({ children, className }) => {
            const isInline = !className;
            if (isInline) {
              return <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono text-gray-800">{children}</code>;
            }
            // Código em bloco
            return <code className="block bg-gray-100 p-2 rounded text-xs font-mono text-gray-800 overflow-x-auto">{children}</code>;
          },
          
          // Citações e blocos de destaque
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-blue-400 bg-blue-50 pl-4 pr-3 py-2 italic text-gray-700 mb-3 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 text-lg">"</span>
                <div className="flex-1">{children}</div>
              </div>
            </blockquote>
          ),
          
          // Links
          a: ({ children, href }) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {children}
            </a>
          ),
          
          // Tabelas (se necessário)
          table: ({ children }) => (
            <table className="min-w-full border-collapse border border-gray-300 mb-2">
              {children}
            </table>
          ),
          th: ({ children }) => (
            <th className="border border-gray-300 px-2 py-1 bg-gray-50 font-semibold text-xs text-gray-700">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-gray-300 px-2 py-1 text-xs text-gray-900">
              {children}
            </td>
          ),
          
          // Linha horizontal
          hr: () => <hr className="my-3 border-gray-300" />,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MessageRenderer;
